import type { DomainCageOverview } from '@/domain/cages/overview.types';
import { formatMachineNumber } from '@/utils/localeFormatters';

/** Columns from the location and cage, prefixed the way the download helper expects. */
const LOCATION_INFO_COLUMNS = [
    'location.name',
    'location.timezone',
    'location.sWeightPct',
    'cage.name',
];

export interface OverviewDownloadRow {
    date: string;
    daypartTag: string;
    measurementsCount: number | null;
    weightAvg: string | undefined;
    weightTrend: string | undefined;
    cfAvg: string | undefined;
    cfTrend: string | undefined;
    fishLengthAvg: string | undefined;
    fishHeightAvg: string | undefined;
    cv: string | undefined;
    weightGrowthAvg: string | undefined;
    weightGrowthTrend: string | undefined;
    depthMin: string | undefined;
    depthMax: string | undefined;
    depthAvg: string | undefined;
}

export interface OverviewDownload {
    rows: OverviewDownloadRow[];
    columns: string[];
}

function toFixed2(value: number | null | undefined): string | undefined {
    return formatMachineNumber(value, 2);
}

/**
 * The overview export: one row per measured day in the selected daypart.
 *
 * Depth ships in the bioscope block, so the two are joined by day and daypart rather than read
 * from one flat row. The data columns are named after the row's own keys, so the header always
 * matches what is in the rows; with no measured day at all only the location columns remain.
 */
export function buildOverviewDownload(
    overview: DomainCageOverview,
    daypartTag: string
): OverviewDownload {
    const depthByDay = new Map(
        overview.bioscope.days.map((day) => [`${day.day}:${day.daypartTag}`, day.data])
    );

    const allRows = overview.biomass.days.map<OverviewDownloadRow>((day) => {
        const depth = depthByDay.get(`${day.day}:${day.daypartTag}`);
        return {
            date: day.day,
            daypartTag: day.daypartTag,
            measurementsCount: day.data.measCnt,
            weightAvg: toFixed2(day.data.weightAvg),
            weightTrend: toFixed2(day.data.weightTrend),
            cfAvg: toFixed2(day.data.cfAvg),
            cfTrend: toFixed2(day.data.cfTrend),
            fishLengthAvg: toFixed2(day.data.fishLengthAvg),
            fishHeightAvg: toFixed2(day.data.fishHeightAvg),
            cv: toFixed2(day.data.cv),
            weightGrowthAvg: toFixed2(day.data.weightGrowthAvg),
            weightGrowthTrend: toFixed2(day.data.weightGrowthTrend),
            depthMin: toFixed2(depth?.depthMin),
            depthMax: toFixed2(depth?.depthMax),
            depthAvg: toFixed2(depth?.depthAvg),
        };
    });

    const dataColumns =
        allRows.length > 0 ? Object.keys(allRows[0]).map((key) => `day.${key}`) : [];

    return {
        rows: allRows.filter((row) => row.daypartTag === daypartTag),
        columns: [...LOCATION_INFO_COLUMNS, ...dataColumns],
    };
}
